import { Receipt, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Field } from '@/components/common/FormBits'
import { apiErrorMessage } from '@/i18n'
import { fmtSize } from '../../meetingFiles'
import {
  EXPENSE_CATEGORIES,
  uploadExpenseFile,
  useDeleteExpenseFile,
  useExpenseAction,
  usePaymentOptions,
  useSaveExpense,
  type CustomerExpense,
  type ExpenseInput,
} from '../../expenses'
import { toastError, useCrm } from '../../ui'
import { DocumentFields, PaymentMethodPicker } from './ExpensePaymentFields'
import { ReceiptDrop, type PendingReceipt } from './ReceiptDrop'
const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' })

/**
 * Müşteri gideri formu: müşteri, başlık, kategori, tutar, tarih, ödeme şekli (şirket kartında yalnız size zimmetli kartlar,
 * iş avansında açık bakiye), belge türü ve faturada KDV, açıklama ve fiş/dekont (zorunlu). "Kaydet ve onaya gönder" belgeleri yükleyip firma sahiplerine gönderir.
 */
export function ExpenseFormModal({ expense, accountId, meetingId, onClose }: { expense?: CustomerExpense; accountId?: string; meetingId?: string; onClose: () => void }) {
  const { t } = useTranslation()
  const { customers } = useCrm()
  const save = useSaveExpense()
  const action = useExpenseAction()
  const delFile = useDeleteExpenseFile()
  const options = usePaymentOptions()
  const [v, setV] = useState<ExpenseInput>({
    accountId: expense?.accountId ?? accountId ?? '',
    meetingId: expense?.meetingId ?? meetingId ?? '',
    title: expense?.title ?? '',
    description: expense?.description ?? '',
    category: expense?.category ?? 'yemek',
    amount: expense?.amount ?? '',
    spentOn: expense?.spentOn ?? today(),
    paymentMethod: expense?.paymentMethod ?? 'cash',
    cardId: expense?.cardId ?? '',
    documentType: expense?.documentType ?? 'receipt',
    vatRate: expense?.vatRate ?? '0',
    invoiceNo: expense?.invoiceNo ?? '',
  })
  const [receipts, setReceipts] = useState<PendingReceipt[]>([])
  const [busy, setBusy] = useState(false)
  const existing = expense?.files ?? []
  const fileTotal = existing.length + receipts.filter((r) => r.state !== 'done').length
  const set = (p: Partial<ExpenseInput>) => setV((x) => ({ ...x, ...p }))
  const amount = Number(v.amount.replace(',', '.'))
  const amountOk = amount > 0
  const baseOk = !!v.accountId && v.title.trim().length >= 2 && amountOk && !!v.spentOn && (v.paymentMethod !== 'company_card' || !!v.cardId)
  const submitOk = baseOk && v.description.trim().length >= 3 && fileTotal > 0 && (v.documentType !== 'invoice' || !!v.invoiceNo.trim())
  const infoRequested = expense?.status === 'info_requested'

  const run = async (submit: boolean) => {
    setBusy(true)
    try {
      const id = await save.mutateAsync({ id: expense?.id, input: v })
      let failed = 0
      for (const r of receipts) {
        if (r.state === 'done') continue
        setReceipts((xs) => xs.map((x) => (x.key === r.key ? { ...x, state: 'uploading', error: undefined } : x)))
        try {
          await uploadExpenseFile(id, r.file)
          setReceipts((xs) => xs.map((x) => (x.key === r.key ? { ...x, state: 'done' } : x)))
        } catch (e) {
          failed++
          setReceipts((xs) => xs.map((x) => (x.key === r.key ? { ...x, state: 'error', error: apiErrorMessage(e) } : x)))
        }
      }
      if (failed) {
        toast.error(t('crm.expenses.someFailed', { count: failed }))
        return
      }
      if (submit && !infoRequested) await action.mutateAsync({ id, action: 'submit' })
      toast.success(t(submit && !infoRequested ? 'crm.expenses.submitted' : 'crm.expenses.saved'))
      onClose()
    } catch (e) {
      toastError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      size="xl"
      dismissible={!busy}
      onOpenChange={(o) => !o && !busy && onClose()}
      icon={<Receipt />}
      title={expense ? t('crm.expenses.editTitle') : t('crm.expenses.newTitle')}
      description={t('crm.expenses.formHint')}
      footer={
        <>
          <Button variant="cancel" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="outline" onClick={() => void run(false)} disabled={!baseOk || busy}>
            {infoRequested ? t('common.save') : t('crm.expenses.saveDraft')}
          </Button>
          {!infoRequested ? (
            <Button onClick={() => void run(true)} disabled={!submitOk || busy}>
              {t('crm.expenses.saveSubmit')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field id="ex-account" label={t('crm.expenses.customer')} full>
            <Select
              id="ex-account"
              searchable
              disabled={!!expense || !!accountId}
              value={v.accountId}
              onChange={(accountId) => set({ accountId })}
              options={[{ value: '', label: t('common.select') }, ...customers.map((c) => ({ value: c.id, label: c.name }))]}
            />
          </Field>
          <Field id="ex-title" label={t('crm.expenses.title')}>
            <Input id="ex-title" value={v.title} maxLength={200} placeholder={t('crm.expenses.titlePlaceholder')} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <Field id="ex-cat" label={t('crm.expenses.category')}>
            <Select id="ex-cat" value={v.category} onChange={(c) => set({ category: c as ExpenseInput['category'] })} options={EXPENSE_CATEGORIES.map((c) => ({ value: c, label: t(`crm.expenses.categories.${c}`) }))} />
          </Field>
          <Field id="ex-amount" label={t('crm.expenses.amount')}>
            <NumberInput id="ex-amount" value={v.amount} onChange={(amount) => set({ amount })} minDecimalsOnBlur={2} suffix="₺" />
          </Field>
          <Field id="ex-date" label={t('crm.expenses.spentOn')}>
            <DateField id="ex-date" value={v.spentOn} max={today()} onChange={(spentOn) => set({ spentOn })} clearable={false} />
          </Field>
        </div>
        <PaymentMethodPicker
          value={v.paymentMethod}
          cardId={v.cardId}
          cards={options.data?.cards ?? []}
          advanceBalance={options.data?.advanceBalance ?? '0.00'}
          amount={amount}
          onChange={set}
        />
        <DocumentFields documentType={v.documentType} vatRate={v.vatRate} invoiceNo={v.invoiceNo} amount={amount} category={v.category} idPrefix="ex" onChange={set} />
        <Field id="ex-desc" label={t('crm.expenses.description')} hint={t('crm.expenses.descriptionHint')}>
          <Textarea id="ex-desc" rows={3} maxLength={4000} value={v.description} onChange={(e) => set({ description: e.target.value })} />
        </Field>
        <div className="space-y-2">
          <p className="text-sm font-medium">{t('crm.expenses.receipts')}</p>
          {existing.length ? (
            <ul className="space-y-1">
              {existing.map((f) => (
                <li key={f.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                  <Receipt className="size-4 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-muted-foreground">{fmtSize(f.size)}</span>
                  {expense?.canEdit ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      aria-label={t('common.delete')}
                      onClick={() => delFile.mutate({ id: expense.id, fileId: f.id }, { onError: toastError })}
                    >
                      <Trash2 />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          <ReceiptDrop items={receipts} onChange={setReceipts} max={10 - existing.length} disabled={busy} />
          {!fileTotal ? <p className="text-xs text-warning">{t('crm.expenses.receiptRequired')}</p> : null}
        </div>
      </div>
    </Modal>
  )
}
